/**
 * Change Log Service
 * 
 * Handles all change log/versioning API calls
 * Used across all exhibits (EX2-EX9) to fetch change history
 */

import { apiRequest } from './apiClient';
import { SyncfusionGridParams } from './types';

// ========================================
// Type Definitions
// ========================================

/**
 * Action types mapping for each exhibit
 */
export type ActionType = 
  | 'financialStatement'  // EX2
  | 'hierarchy'           // EX3
  | 'riskAssessment'      // EX4
  | 'control'             // EX5
  | 'gap'                 // EX6
  | 'certification'       // EX7
  | 'compliance'          // EX8
  | 'auditPlan'          // EX9
  | 'userManagement';    // Admin Panel User Management

/**
 * Change log item from API response
 */
export interface ChangeLogItem {
  Id: string;
  Title: string;
  Description: string;
  OldValue: string;
  NewValue: string;
  ActionType: string;
  ActionedById: string;
  ActionedByName: string;
  CreatedAt: string;
}

/**
 * Parsed change log item for UI
 */
export interface ParsedChangeLogItem {
  id: string;
  title: string;
  description: string;
  oldValue: string;
  newValue: string;
  actionType: string;
  actionedById: string;
  actionedByName: string;
  createdAt: Date;
  // Formatted fields for UI
  formattedDate: string;  // "1 AUG, 2023"
  formattedTime: string;  // "12:05PM"
}

/**
 * API response structure
 */
export interface GetChangeLogResponse {
  result: ChangeLogItem[];
  count: number;
}

// ========================================
// Helper Functions
// ========================================

/**
 * Format date to display format "1 AUG, 2023"
 */
const formatDate = (date: Date): string => {
  const day = date.getDate();
  const month = date.toLocaleString('en-US', { month: 'short' }).toUpperCase();
  const year = date.getFullYear();
  return `${day} ${month}, ${year}`;
};

/**
 * Format time to display format "12:05PM"
 */
const formatTime = (date: Date): string => {
  return date.toLocaleString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).replace(' ', '');
};

/**
 * Parse backend change log item to frontend format
 */
export const parseChangeLogItem = (item: ChangeLogItem): ParsedChangeLogItem => {
  const createdAt = new Date(item.CreatedAt);
  
  return {
    id: item.Id,
    title: item.Title,
    description: item.Description,
    oldValue: item.OldValue,
    newValue: item.NewValue,
    actionType: item.ActionType,
    actionedById: item.ActionedById,
    actionedByName: item.ActionedByName,
    createdAt,
    formattedDate: formatDate(createdAt),
    formattedTime: formatTime(createdAt),
  };
};

/**
 * Group change log items by date
 */
export const groupChangeLogByDate = (
  items: ParsedChangeLogItem[]
): Map<string, ParsedChangeLogItem[]> => {
  const grouped = new Map<string, ParsedChangeLogItem[]>();
  
  // Sort items by date descending (newest first)
  const sortedItems = [...items].sort(
    (a, b) => b.createdAt.getTime() - a.createdAt.getTime()
  );
  
  for (const item of sortedItems) {
    const dateKey = item.formattedDate;
    if (!grouped.has(dateKey)) {
      grouped.set(dateKey, []);
    }
    grouped.get(dateKey)!.push(item);
  }
  
  return grouped;
};

// ========================================
// API Functions
// ========================================

/**
 * Get change log for a specific exhibit
 * 
 * @param annualReportId - The annual report ID
 * @param actionType - The action type for the exhibit
 * @param request - Pagination parameters (optional, defaults to load all)
 * @returns Promise with parsed change log items
 */
export const getChangeLog = async (
  annualReportId: string,
  actionType: ActionType,
  request: SyncfusionGridParams = { skip: 0, take: 1000, requiresCounts: true, sorted: [{ name: 'CreatedAt', direction: 'descending' as const }] }
): Promise<{ items: ParsedChangeLogItem[]; count: number }> => {
  try {
    const response = await apiRequest<GetChangeLogResponse, SyncfusionGridParams>(
      'POST',
      `/change-log/actions/index-grid/${annualReportId}/${actionType}`,
      request,
      true // Authentication required
    );
    
    const parsedItems = response.result.map(parseChangeLogItem);
    
    return {
      items: parsedItems,
      count: response.count,
    };
  } catch (error) {
    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }
    throw {
      code: 'UNEXPECTED_ERROR',
      message: error instanceof Error ? error.message : 'Failed to fetch change log',
    };
  }
};

export const getAdminUserChangeLog = async (
  request: SyncfusionGridParams = { skip: 0, take: 1000, requiresCounts: true, sorted: [{ name: 'CreatedAt', direction: 'descending' as const }] }
): Promise<{ items: ParsedChangeLogItem[]; count: number }> => {
  try {
    const response = await apiRequest<GetChangeLogResponse, SyncfusionGridParams>(
      'POST',
      `/change-log/admin/actions/user-management/index-grid`,
      request,
      true // Authentication required
    );
    
    const parsedItems = response.result.map(parseChangeLogItem);
    
    return {
      items: parsedItems,
      count: response.count,
    };
  } catch (error) {
    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }
    throw {
      code: 'UNEXPECTED_ERROR',
      message: error instanceof Error ? error.message : 'Failed to fetch change log',
    };
  }
};

/**
 * Helper to get display name for action type
 */
export const getActionTypeDisplayName = (actionType: ActionType): string => {
  const displayNames: Record<ActionType, string> = {
    financialStatement: 'Financial Statements',
    hierarchy: 'Process Hierarchy',
    riskAssessment: 'Risk Assessment',
    control: 'Control Activities',
    gap: 'Gaps',
    certification: 'Certification',
    compliance: 'COSO Compliance',
    auditPlan: 'Audit Plan',
    userManagement: 'User Management'
  };
  return displayNames[actionType];
};
